import { Button, Input, useToast } from "@/components/ui";
import { useResetPassword } from "@/lib/react-query/queries";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui";
import { Loader } from "@/components/shared";
import { ResetPasswordValidation } from "@/lib/validation";
import { z } from "zod";


const ResetPassword = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm({
    defaultValues: {
      password: "",
      confirmPassword: ""
    },
  });

  const { mutateAsync: resetPassword } = useResetPassword();

  const handleResetPassword = async (values: z.infer<typeof ResetPasswordValidation>) => {
    if (values.password !== values.confirmPassword) {
      toast({ 
        title: "Passwords don't match", 
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      // Get userId and secret from URL params
      const params = new URLSearchParams(window.location.search);
      const userId = params.get('userId') || '';
      const secret = params.get('secret') || '';

      await resetPassword({ 
        userId, 
        secret, 
        password: values.password 
      });
      
      toast({
        title: "Password reset successful!",
        description: "You can now sign in with your new password."
      });
      
      navigate('/sign-in');
    } catch (error) {
      toast({ 
        title: "Password reset failed", 
        description: error instanceof Error ? error.message : "Please try again later",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form {...form}>
      <div className="sm:w-420 flex-center flex-col">
        <img src="/assets/images/logo.svg" alt="logo" />

        <h2 className="h3-bold md:h2-bold pt-5 sm:pt-12">
          Create New Password
        </h2>
        <p className="text-light-3 small-medium md:base-regular mt-2">
          Enter your new password below
        </p>

        <form
          onSubmit={form.handleSubmit(handleResetPassword)}
          className="flex flex-col gap-5 w-full mt-4">
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="shad-form_label">New Password</FormLabel>
                <FormControl>
                  <Input 
                    type="password" 
                    className="shad-input" 
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="shad-form_label">Confirm New Password</FormLabel>
                <FormControl>
                  <Input 
                    type="password" 
                    className="shad-input" 
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button 
            type="submit" 
            className="shad-button_primary"
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="flex-center gap-2">
                <Loader /> Resetting...
              </div>
            ) : (
              "Reset Password"
            )}
          </Button>
        </form>
      </div>
    </Form>
  );
};


export default ResetPassword
